// Pure presentation helpers for the Financial Control dashboard. No React here.
// Money/percentage/tone primitives are shared with the other cost-control tabs via ./money.
import { humanEUR, pct, statusTone } from "./money";

export { humanEUR, statusTone };

// breachLabel renders the "Forecast breach" cell: "at month-end" when the
// projected breach lands on (or after) the last remaining day of the period,
// otherwise "in N days"; "—" when no breach is projected.
export function breachLabel(daysToBreach, period, asOf) {
  if (daysToBreach == null) return "—";
  const [y, m] = (period || "").split("-").map(Number);
  if (!y || !m) return `in ${daysToBreach} days`;
  const daysIn = new Date(y, m, 0).getDate();
  const now = asOf ? new Date(asOf) : new Date();
  const elapsed =
    now.getUTCFullYear() === y && now.getUTCMonth() + 1 === m
      ? Math.min(Math.max(now.getUTCDate(), 1), daysIn)
      : daysIn;
  const remaining = daysIn - elapsed;
  if (daysToBreach >= remaining) return "at month-end";
  const n = daysToBreach;
  return `in ${n} ${n === 1 ? "day" : "days"}`;
}

// confidenceNote turns the spend KPI's confidence fields into a plain-language line
// for forecast tooltips. Basis-aware: "month-end projection" accuracy vs "day-to-day"
// model error. Soft "not yet rated" when there's no rating.
export function confidenceNote(spend = {}) {
  const basis = spend && spend.confidence_basis;
  const level = spend && spend.confidence_level;
  if (!basis || basis === "not_rated" || !level) {
    return "Forecast accuracy: not yet rated (needs a few months of history).";
  }
  const cap = level.charAt(0).toUpperCase() + level.slice(1);
  const kind = basis === "projection" ? "month-end projection" : "day-to-day";
  return `Forecast confidence: ${cap} (${kind}).`;
}

// buildTiles maps the kpis payload to an ordered array of tile view-models.
// Each tile: { key, label, value, why, tone, href }.
export function buildTiles(kpis = {}) {
  const spend = kpis.spend_vs_forecast || {};
  const budget = kpis.budget_risk || {};
  const unassigned = kpis.unassigned || {};
  const ai = kpis.ai_share || {};

  // spend: variance — MTD actual vs what the start-of-month forecast expected by today
  // (pro-rated). NOT a budget/plan (there may be no total budget), so it reads as "expected".
  const plan = Number(spend.plan_to_date) || 0;
  let spendValue = humanEUR(spend.actual);
  let spendWhy = "forecast warming up";
  if (plan > 0) {
    const v = Math.round((Number(spend.variance) || 0) * 100);
    spendValue = `${v >= 0 ? "+" : ""}${v}%`;
    spendWhy = `${humanEUR(spend.actual)} vs ${humanEUR(plan)} expected so far`;
  }

  // budget: portfolio health driven by budget_risk.counts
  const c = budget.counts || {};
  const onTrack = c.on_track || 0,
    atRisk = c.at_risk || 0,
    over = c.over || 0,
    notMeasurable = c.not_measurable || 0;
  const total = onTrack + atRisk + over + notMeasurable;
  let budgetValue = "On track";
  if (over > 0) budgetValue = `${over} over`;
  else if (atRisk > 0) budgetValue = `${atRisk} at risk`;
  let budgetWhy = `${onTrack} of ${total} on track`;
  if (notMeasurable > 0) budgetWhy += ` · ${notMeasurable} not measurable`;
  let budgetTone = "ok";
  if (over > 0) budgetTone = "critical";
  else if (atRisk > 0) budgetTone = "attention";
  else if (notMeasurable > 0) budgetTone = "attention";

  // AI: hidden entirely when no AI source is configured; otherwise show the
  // tracking state. Muted whenever there's no AI spend this period.
  const aiConfigured = !!ai.ai_configured;
  let aiTile = null;
  if (aiConfigured) {
    let aiValue = humanEUR(ai.cost || 0);
    let aiWhy;
    let aiTone = "neutral";
    let aiMuted = false;
    if ((ai.cost || 0) === 0) {
      aiWhy = "tracking active · €0 so far";
      aiMuted = true;
    } else if (!ai.prev_cost) {
      aiWhy = "new this month";
    } else {
      const momPct = Math.round((ai.mom_pct || 0) * 100);
      aiWhy = `${momPct >= 0 ? "▲" : "▼"}${Math.abs(momPct)}% vs last month`;
      if ((ai.mom_pct || 0) > 0.25) aiTone = "attention";
    }
    aiTile = { key: "ai", label: "AI", value: aiValue, why: aiWhy, tone: aiTone, href: "/cost-analyzer", muted: aiMuted };
  }

  const mv = kpis.biggest_mover || {};
  const hasMover = !!mv.name && Math.round(mv.amount || 0) >= 1;
  const moverValue = hasMover ? mv.name : "Nothing notable";
  const moverWhy = hasMover ? `+${humanEUR(mv.amount)} this week` : "no spike this week";

  const unassignedZero = !(Number(unassigned.cost) > 0);
  // Window the unassigned € is measured over (trailing days), shown so the CFO knows the
  // basis and can reconcile it with the /allocation ownership page (same window).
  const unassignedWindow = `last ${unassigned.window_days || 30}d`;
  const unassignedInfo = `Spend with no Organisation owner + spend not covered by any source, over the ${unassignedWindow}. Matches the Ownership page (/allocation).`;
  const FORECAST_INFO =
    "Expected = total cloud & AI spend, projected to month-end (updated nightly) and pro-rated to today.";
  // Carry the forecast's track record (confidence/MAPE) on a SECOND line below the
  // explanation (the shared tooltip splits on "\n") so a CFO can judge how much to
  // trust the projection — on hover only.
  const spendInfo = `${FORECAST_INFO}\n${confidenceNote(spend)}`;

  const tiles = [
    { key: "budget", label: "Budgets", value: budgetValue, why: budgetWhy, tone: budgetTone, href: "/budgets", link: { label: "Review budget →", to: "/budgets" } },
    { key: "spend", label: "Spend vs expected", value: spendValue, why: spendWhy, tone: statusTone(spend.status), href: "/cost-analyzer", info: spendInfo },
    { key: "unassigned", label: "Unassigned", value: humanEUR(unassigned.cost), why: unassignedZero ? `all spend owned · ${unassignedWindow}` : `${pct(unassigned.pct)} has no owner · ${unassignedWindow}`, tone: statusTone(unassigned.status), href: "/allocation", muted: unassignedZero, info: unassignedInfo, link: { label: "Review unassigned →", to: "/allocation" } },
    aiTile,
    { key: "biggest-mover", label: "Biggest mover", value: moverValue, why: moverWhy, tone: statusTone(mv.status), href: "/cost-analyzer", muted: !hasMover },
  ];
  return tiles.filter(Boolean);
}
